
import React, { useState } from 'react';
import { Palette, Type, Image, Download } from 'lucide-react';
import { ReportTemplate } from './ReportTemplateSelector';

interface ReportCustomizerProps {
  template: ReportTemplate;
  title: string;
  content: string;
  onCustomize: (customizations: ReportCustomizations) => void;
  onExport: (format: 'pdf' | 'docx' | 'pptx') => void;
  onClose: () => void;
}

export interface ReportCustomizations {
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  logo?: string;
  organizationName?: string;
  headerStyle: string;
  includeTableOfContents: boolean;
  includeExecutiveSummary: boolean;
  pageNumbers: boolean;
}

const ReportCustomizer: React.FC<ReportCustomizerProps> = ({ 
  template, 
  title, 
  content, 
  onCustomize, 
  onExport, 
  onClose 
}) => {
  const [customizations, setCustomizations] = useState<ReportCustomizations>({
    primaryColor: template.styles.primaryColor,
    accentColor: template.styles.accentColor,
    fontFamily: template.styles.fontFamily,
    organizationName: '',
    headerStyle: template.styles.headerStyle,
    includeTableOfContents: true,
    includeExecutiveSummary: true,
    pageNumbers: true
  });

  const handleCustomizationChange = (key: keyof ReportCustomizations, value: any) => {
    const updated = { ...customizations, [key]: value };
    setCustomizations(updated);
    onCustomize(updated);
  };

  const colorPresets = [
    { name: 'Professional Blue', primary: '#1e40af', accent: '#3b82f6' },
    { name: 'Executive Red', primary: '#dc2626', accent: '#ef4444' },
    { name: 'Strategic Green', primary: '#059669', accent: '#10b981' },
    { name: 'Corporate Purple', primary: '#7c3aed', accent: '#a855f7' },
    { name: 'Modern Orange', primary: '#ea580c', accent: '#f97316' }
  ];

  const fontOptions = [
    'Inter, sans-serif',
    'system-ui, sans-serif',
    'Georgia, serif',
    'Times New Roman, serif',
    'Arial, sans-serif'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-semibold text-primary-800">Customize Report</h2>
            <p className="text-neutral-600 mt-1">Personalize your {template.name} template</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="flex flex-grow overflow-hidden">
          {/* Customization Panel */}
          <div className="w-1/3 p-6 border-r overflow-y-auto">
            <div className="space-y-6">
              {/* Branding Section */}
              <div>
                <h3 className="flex items-center text-lg font-medium text-neutral-800 mb-4">
                  <Image className="h-5 w-5 mr-2" />
                  Branding
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      value={customizations.organizationName || ''}
                      onChange={(e) => handleCustomizationChange('organizationName', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="Your Organization"
                    />
                  </div>
                </div>
              </div>

              {/* Color Section */}
              <div>
                <h3 className="flex items-center text-lg font-medium text-neutral-800 mb-4">
                  <Palette className="h-5 w-5 mr-2" />
                  Colors
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-2">
                    {colorPresets.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          handleCustomizationChange('primaryColor', preset.primary);
                          handleCustomizationChange('accentColor', preset.accent);
                        }}
                        className="flex items-center p-2 border border-neutral-200 rounded-md hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex space-x-1 mr-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                        </div>
                        <span className="text-sm text-neutral-700">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Typography Section */}
              <div>
                <h3 className="flex items-center text-lg font-medium text-neutral-800 mb-4">
                  <Type className="h-5 w-5 mr-2" />
                  Typography
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={customizations.fontFamily}
                      onChange={(e) => handleCustomizationChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {fontOptions.map((font) => (
                        <option key={font} value={font}>
                          {font.split(',')[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Layout Options */}
              <div>
                <h3 className="text-lg font-medium text-neutral-800 mb-4">Layout Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customizations.includeTableOfContents}
                      onChange={(e) => handleCustomizationChange('includeTableOfContents', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Include Table of Contents</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customizations.includeExecutiveSummary}
                      onChange={(e) => handleCustomizationChange('includeExecutiveSummary', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Include Executive Summary</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customizations.pageNumbers}
                      onChange={(e) => handleCustomizationChange('pageNumbers', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-neutral-700">Page Numbers</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="flex-grow p-6 overflow-y-auto bg-neutral-50">
            <div className="bg-white rounded-lg shadow-sm p-8 max-w-none" style={{ fontFamily: customizations.fontFamily }}>
              <div className="border-b-4 pb-4 mb-6" style={{ borderColor: customizations.primaryColor }}>
                {customizations.organizationName && (
                  <div className="text-sm text-neutral-600 mb-2">{customizations.organizationName}</div>
                )}
                <h1 className="text-3xl font-bold" style={{ color: customizations.primaryColor }}>
                  {title}
                </h1>
                <div className="text-sm text-neutral-500 mt-2">
                  Generated on {new Date().toLocaleDateString()}
                </div>
              </div>
              
              {customizations.includeExecutiveSummary && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold mb-3" style={{ color: customizations.accentColor }}>
                    Executive Summary
                  </h2>
                  <div className="bg-neutral-50 p-4 rounded-md border-l-4" style={{ borderColor: customizations.accentColor }}>
                    <p className="text-neutral-700 text-sm">
                      This section will contain a concise overview of the key findings and recommendations from the political campaign analysis.
                    </p>
                  </div>
                </div>
              )}

              <div className="prose max-w-none">
                <div className="text-neutral-800">
                  {content.split('\n').slice(0, 10).map((paragraph, index) => {
                    if (paragraph.startsWith('# ')) {
                      return (
                        <h2 key={index} className="text-xl font-semibold mt-6 mb-3" style={{ color: customizations.primaryColor }}>
                          {paragraph.substring(2)}
                        </h2>
                      );
                    }
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h3 key={index} className="text-lg font-semibold mt-5 mb-2" style={{ color: customizations.accentColor }}>
                          {paragraph.substring(3)}
                        </h3>
                      );
                    }
                    return paragraph ? <p key={index} className="mb-4 text-sm">{paragraph}</p> : <br key={index} />;
                  })}
                  <div className="text-center text-neutral-400 text-sm mt-8">
                    ... Preview of first 10 sections ...
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center p-6 border-t bg-white">
          <div className="text-sm text-neutral-600">
            Preview shows the first sections of your formatted report
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
            >
              Cancel
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => onExport('pdf')}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                PDF
              </button>
              <button
                onClick={() => onExport('docx')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                DOCX
              </button>
              <button
                onClick={() => onExport('pptx')}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                PPTX
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCustomizer;
